import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

interface ErrorBoundaryProps {
  children: ReactNode
  level: 'app' | 'layout' | 'feature'
  fallback?: ReactNode
}

/**
 * Base error boundary class.
 *
 * All caught errors are logged to the API (fire-and-forget) so we have
 * visibility into production client-side crashes without blocking recovery.
 *
 * Three levels exist so failures are isolated at the right granularity:
 * - app: full-page error (only for catastrophic failures)
 * - layout: content area error (sidebar/header remain functional)
 * - feature: inline error (rest of the page remains functional)
 */
class ErrorBoundaryBase extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[ErrorBoundary:${this.props.level}]`, error, info)

    // Fire-and-forget error logging to API
    const apiUrl = import.meta.env.VITE_API_URL
    if (apiUrl) {
      fetch(`${apiUrl}/api/errors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          level: this.props.level,
          message: error.message,
          stack: error.stack,
          componentStack: info.componentStack,
          url: window.location.href,
          timestamp: new Date().toISOString(),
        }),
      }).catch(() => {
        // Logging failure must not cause another error
      })
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (!this.state.hasError) return this.props.children
    if (this.props.fallback) return this.props.fallback

    if (this.props.level === 'app') {
      return <AppErrorFallback error={this.state.error} onReset={this.handleReset} />
    }
    if (this.props.level === 'layout') {
      return <LayoutErrorFallback error={this.state.error} onReset={this.handleReset} />
    }
    return <FeatureErrorFallback error={this.state.error} onReset={this.handleReset} />
  }
}

// ── Fallback UIs ─────────────────────────────────────────────────────────────

function AppErrorFallback({ error, onReset }: { error: Error | null; onReset: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="text-center max-w-md">
        <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
        <p className="text-muted-foreground mb-6 text-sm">
          {error?.message ?? 'An unexpected error occurred. Please try reloading.'}
        </p>
        <div className="flex gap-3 justify-center">
          <Button onClick={onReset} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Try again
          </Button>
          <Button onClick={() => window.location.reload()}>Reload page</Button>
        </div>
      </div>
    </div>
  )
}

function LayoutErrorFallback({ error, onReset }: { error: Error | null; onReset: () => void }) {
  return (
    <div className="flex items-center justify-center h-full min-h-64 p-8">
      <div className="text-center max-w-md">
        <AlertTriangle className="h-10 w-10 text-destructive mx-auto mb-3" />
        <h2 className="text-lg font-semibold mb-2">Page failed to load</h2>
        <p className="text-muted-foreground mb-4 text-sm">
          {error?.message ?? 'An error occurred loading this page.'}
        </p>
        <Button onClick={onReset} variant="outline" size="sm">
          <RefreshCw className="mr-2 h-4 w-4" />
          Try again
        </Button>
      </div>
    </div>
  )
}

function FeatureErrorFallback({ error, onReset }: { error: Error | null; onReset: () => void }) {
  return (
    <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 flex items-start gap-3">
      <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-destructive">
          {error?.message ?? 'Something went wrong'}
        </p>
        <button
          onClick={onReset}
          className="mt-1 text-xs text-muted-foreground hover:text-foreground underline"
        >
          Try again
        </button>
      </div>
    </div>
  )
}

// ── Named exports for each level ─────────────────────────────────────────────

export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return <ErrorBoundaryBase level="app">{children}</ErrorBoundaryBase>
}

export function LayoutErrorBoundary({ children }: { children: ReactNode }) {
  return <ErrorBoundaryBase level="layout">{children}</ErrorBoundaryBase>
}

export function FeatureErrorBoundary({ children }: { children: ReactNode }) {
  return <ErrorBoundaryBase level="feature">{children}</ErrorBoundaryBase>
}
